// DE-SEAT-OWNER phase 2 (design 2026-11-02 "DEs as seat owners" WP3): what the client shows of a
// digital employee that holds a seat on its own.
//   - the chart chips: "Acting" (a DE covering a vacant seat) and "Lead (digital)" (a DE that
//     owns a seat leading a team), plus "accountable: <name>", the person who answers for the seat;
//   - the seat drawer's switch (assist · act when vacant · own) with a one-line explanation each;
//   - the hand-back dialog shown before a person is assigned to a seat a DE is acting in.
// Everything here is shown only where the API sends a capacity or an occupancy (company flag
// `de_seat_owner` on); with it off nothing renders.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Segmented,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { SeatDetail } from "../../api/types";
import type { CompanyAccess } from "../company";
import { seatQuery, structureKeys } from "../queries";
import { FormError } from "../structure/parts";
import type { ChartSeat } from "./chart-model";
import { chartKeys } from "./chart-queries";

export type DeFill = "assist" | "act_when_vacant" | "own";

/**
 * What a seat's switch reads while it has never been set. Andrei has not decided the default for
 * a new assistant (design §7.2 recommends "act_when_vacant"); until he does it is "assist", and
 * this is the only place that says so.
 */
export const DEFAULT_DE_FILL: DeFill = "assist";

export const DE_FILL_OPTIONS: { value: DeFill; label: string; explain: string }[] = [
  {
    value: "assist",
    label: "Assist",
    explain:
      "The digital employee helps whoever holds the seat; when they leave, the seat is open.",
  },
  {
    value: "act_when_vacant",
    label: "Act when vacant",
    explain: "The digital employee acts as owner while no person holds the seat.",
  },
  {
    value: "own",
    label: "Own",
    explain:
      "The digital employee owns the seat and no person is expected; it cannot take a person.",
  },
];

type ChartHolder = ChartSeat["holders"][number];
const isDe = (h: ChartHolder) => h.membership_kind === "digital_employee";
const isPerson = (h: ChartHolder) => h.membership_kind === "person";

export type OwnerChip = "acting" | "lead";

/** The chip a DE-held seat wears: null for a person's seat, an assistant or an owner. */
export function ownerChip(
  seat: Pick<ChartSeat, "id" | "holders">,
  leadSeatIds: ReadonlySet<string>,
): OwnerChip | null {
  if (seat.holders.some(isPerson)) return null;
  const de = seat.holders.find(isDe);
  if (!de) return null;
  if (de.capacity === "acting") return "acting";
  if (de.capacity === "owner" && leadSeatIds.has(seat.id)) return "lead";
  return null;
}

/** Whether a DE holds the seat as acting owner or owner (no person): the seat has an accountable. */
export function deHeld(seat: Pick<ChartSeat, "holders">): boolean {
  if (seat.holders.some(isPerson)) return false;
  const de = seat.holders.find(isDe);
  return de?.capacity === "acting" || de?.capacity === "owner";
}

/**
 * The person who answers for a DE-held seat: the first person on the reporting line above it
 * (the chart carries the line, so no call; the server walks the same line for approvals).
 */
export function accountableName(
  seat: Pick<ChartSeat, "supervisor_seat_id">,
  seatById: ReadonlyMap<string, Pick<ChartSeat, "supervisor_seat_id" | "holders">>,
): string | null {
  const seen = new Set<string>();
  let up = seat.supervisor_seat_id;
  while (up && !seen.has(up)) {
    seen.add(up);
    const s = seatById.get(up);
    if (!s) return null;
    const p = s.holders.find(isPerson);
    if (p) return p.display_name;
    up = s.supervisor_seat_id;
  }
  return null;
}

export const OWNER_CHIP_LABELS: Record<OwnerChip, string> = {
  acting: "Acting",
  lead: "Lead (digital)",
};

/** The chart card's chip for a DE-held seat. */
export function OwnerChipBadge({ chip }: { chip: OwnerChip }) {
  return (
    <Badge
      variant={chip === "lead" ? "accent" : "info"}
      data-chip={chip}
      title={
        chip === "acting"
          ? "A digital employee is acting as owner while the seat has no person"
          : "A digital employee owns this seat and leads its team"
      }
    >
      {OWNER_CHIP_LABELS[chip]}
    </Badge>
  );
}

/** "accountable: Maria" under a DE-held card. */
export function AccountableLine({ name }: { name: string | null }) {
  return (
    <span className="min-w-0 truncate text-2xs text-faint" data-slot="accountable">
      {name ? `accountable: ${name}` : "accountable: nobody yet"}
    </span>
  );
}

/** The switch's current value: the seat's own, else the default. */
export const effectiveDeFill = (seat: Pick<SeatDetail, "de_fill">): DeFill =>
  seat.de_fill ?? DEFAULT_DE_FILL;

/**
 * The seat drawer's switch. Shown only for a seat a DE holds, with the flag on (the seat read
 * carries `occupancy`); changed by managers. `own` is refused by the API while a person holds the
 * seat, so the option says so up front and asks for confirmation before it commits.
 */
export function DeFillSwitch({
  api,
  company,
  seat,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  seat: SeatDetail;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const personHere = seat.assignments.some((a) => a.membership_kind === "person");
  const value = effectiveDeFill(seat);
  const save = useMutation({
    mutationFn: async (next: DeFill) => {
      // the version read now, so a change made elsewhere is a conflict, not overwritten
      const fresh = await qc.fetchQuery({ ...seatQuery(api, company.id, seat.id), staleTime: 0 });
      return api.client.seats.update({
        params: { co: company.id, id: seat.id },
        ifMatch: fresh.version,
        body: { de_fill: next },
      });
    },
    onSuccess: () => {
      toast.add({ title: "Seat switch saved", type: "success" });
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
      void qc.invalidateQueries({ queryKey: structureKeys.seat(company.id, seat.id) });
    },
  });
  const [confirmOwn, setConfirmOwn] = useState(false);
  const [refused, setRefused] = useState(false);
  if (seat.occupancy === undefined) return null;
  const current = DE_FILL_OPTIONS.find((o) => o.value === value);
  return (
    <div className="flex flex-col gap-1" data-slot="de-fill-switch">
      {canEdit ? (
        <Segmented
          aria-label="Digital employee on this seat"
          size="sm"
          value={value}
          onValueChange={(next: DeFill) => {
            if (next === value || save.isPending) return;
            setRefused(false);
            if (next === "own" && personHere) setRefused(true);
            else if (next === "own") setConfirmOwn(true);
            else save.mutate(next);
          }}
          options={DE_FILL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
        />
      ) : (
        <span className="text-xs font-medium" data-slot="de-fill-value">
          {current?.label}
        </span>
      )}
      <p className="text-xs text-muted-foreground" data-slot="de-fill-explain">
        {current?.explain}
      </p>
      {refused ? (
        <p className="text-xs text-warning-text" role="alert" data-slot="de-fill-refused">
          A person holds this seat. Unassign them before the digital employee can own it.
        </p>
      ) : null}
      <FormError what="Saving the switch" error={save.error} />
      <AlertDialog open={confirmOwn} onOpenChange={setConfirmOwn}>
        <AlertDialogContent data-slot="de-own-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Let the digital employee own this seat?</AlertDialogTitle>
            <AlertDialogDescription>
              No person is expected to hold it, and a person cannot be assigned until you move the
              switch back. Approvals and escalations still go to the next person up the chart.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOwn(false);
                save.mutate("own");
              }}
            >
              Own this seat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** The switch in the chart's seat panel: reads the seat, renders nothing with the flag off. */
export function SeatDeFill({
  api,
  company,
  seatId,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  canEdit: boolean;
}) {
  const seat = useQuery(seatQuery(api, company.id, seatId));
  if (!seat.data || seat.data.occupancy === undefined) return null;
  return <DeFillSwitch api={api} company={company} seat={seat.data} canEdit={canEdit} />;
}

/** The DE acting in a seat now (its assignment's capacity is `acting`), if any. */
export function actingAssignment(seat: Pick<SeatDetail, "assignments">) {
  return seat.assignments.find(
    (a) => a.membership_kind === "digital_employee" && a.capacity === "acting",
  );
}

/**
 * Before a person is assigned to a seat a DE is acting in: say what happens (the DE stays on as
 * assistant, the seat's open work stays where it is) and let the assigner confirm or cancel.
 */
export function HandBackDialog({
  open,
  deName,
  personName,
  since,
  pending,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  deName: string;
  personName: string;
  since: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(o) => (o ? undefined : onCancel())}>
      <AlertDialogContent data-slot="hand-back-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Hand the seat back to {personName}?</AlertDialogTitle>
          <AlertDialogDescription>
            {deName} has been acting as owner of this seat since {since}. Assigning {personName}{" "}
            hands it back: {personName} owns the seat and answers for it, and {deName} stays on as
            their assistant. Work already open stays where it is.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={pending} onClick={onConfirm}>
            Hand back and assign
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
